import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useNavigate } from "react-router-dom";

import { CREDITS_CHANGED_EVENT } from "@/api/client";
import brainHrLogo from "@/assets/brain-hr-logo.png";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAuthStore } from "@/stores/authStore";

export default function Header() {
  const { t } = useTranslation();
  const { user, logout, loadMe } = useAuthStore();
  const navigate = useNavigate();

  // Balance nach bezahlten Aktionen / 402 / Code-Einlösung aktualisieren
  // (Event feuert der API-Client in api/client.ts).
  useEffect(() => {
    const refresh = () => {
      loadMe();
    };
    window.addEventListener(CREDITS_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(CREDITS_CHANGED_EVENT, refresh);
  }, [loadMe]);

  return (
    <header className="border-b border-ca-border bg-ca-bg/80 backdrop-blur-xl sticky top-0 z-40">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-5 group">
          <span className="relative inline-block w-20 h-20 shrink-0" aria-hidden>
            <img
              src={brainHrLogo}
              alt=""
              className="w-full h-full object-contain"
              // Dark-Theme: Swirl als Ivory-Silhouette (war: Navy — auf
              // navy-deep unsichtbar). Ton ca. #F5F0E5.
              style={{
                filter: "brightness(0) invert(94%) sepia(6%)",
              }}
            />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="text-2xl font-serif font-semibold text-ca-brand tracking-tight">
              Nudging Bloom
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ca-ink-muted mt-1">
              Dr. Franz Hütter
              <span className="text-ca-ink-muted"> · </span>
              BRAIN-HR
            </span>
          </span>
        </Link>
        <div className="flex items-center gap-4 text-sm shrink-0">
          <LanguageSwitcher />
          {user && (
            <>
              {user.role === "admin" && (
                <NavLink
                  to="/admin"
                  className={({ isActive }) =>
                    `text-xs uppercase tracking-widest font-bold transition ${
                      isActive ? "text-ca-accent-text" : "text-ca-ink-muted hover:text-ca-accent-text"
                    }`
                  }
                >
                  {t("nav.admin")}
                </NavLink>
              )}
              <span className="hidden sm:inline text-ca-ink-muted font-mono text-xs">
                {user.email}
                {typeof user.audit_balance === "number" && user.role !== "admin" && (
                  <Link
                    to="/activate"
                    title={t("nav.topup")}
                    className="ml-2 px-2 py-0.5 bg-ca-card text-ca-surface-ink rounded text-[10px] uppercase tracking-wider hover:ring-1 hover:ring-ca-accent transition"
                  >
                    {t("nav.audits", { count: user.audit_balance })}
                  </Link>
                )}
                {user.role === "admin" && (
                  <span className="ml-2 px-2 py-0.5 bg-ca-accent/10 text-ca-accent-text rounded text-[10px] uppercase tracking-wider">
                    admin
                  </span>
                )}
              </span>
              <button
                onClick={async () => {
                  await logout();
                  navigate("/login");
                }}
                className="text-ca-ink-muted hover:text-ca-accent-text text-xs uppercase tracking-widest font-bold transition"
              >
                {t("nav.logout")}
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
